import React, { useState, useCallback, useEffect, useContext } from 'react';
import styled from 'styled-components';
import SideNav from './SideNav';
import { getBaseURL } from '../../services/utils';
import Loading from 'react-loading';
import { AppContext } from '../../App';

const Sight = () => {
  const [search, setSearch] = useState({
    text: '',
    city: '',
    postal: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchMsg, setSearchMsg] = useState('Enter text string to search');

  const {
    view,
    handleView,
    getToken,
    environment,
    sightSearch,
    setSightSearch
  } = useContext(AppContext);

  const handleSearch = useCallback(async ({ text, city, postal }) => {
    if (!text) {
      setSearchMsg('Text required');
      return;
    }
    setLoading(true);
    const { token } = await getToken();
    if (!token) return;
    try {
      const res = await fetch(getBaseURL(environment) + `/merchants/sight`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }),
        body: JSON.stringify({
          textstring: text,
          city,
          postal_code: postal
        })
      })
      if (res.status === 200) {
        const { sight } = await res.json();
        setSearchResults(sight);
        if (!sight.length) {
          setSearchMsg('No results found');
        }
      } else {
        setSearchMsg('No results found');
      }
    }
    catch(err) {
      console.log("Error fetching sight", err)
    }
    finally {
      setLoading(false);
    }
  }, [environment, getToken])

  const handleSightChange = (e) => {
    e.persist()
    setSearch(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }))
  }

  const openUrl = (url) => window.open(url, '_blank')

  useEffect(() => {
    if (sightSearch.text) {
      setSightSearch({
        text: '',
        city: '',
        postal: ''
      })
      setSearch(sightSearch)
      handleSearch(sightSearch);
    }
    // eslint-disable-next-line
  }, [])

  const showResults = () => {
    if(loading) {
      return(
        <StandAloneContainer style={{alignItems: 'flex-start', padding: '2rem'}}>
          <Loading type={'spin'} color={'#424242'} height={30} width={30}/>
        </StandAloneContainer>
      )
    }
    if(searchResults.length) {
      return(
        <Column>
          {searchResults.map(({ name, address, google_id, yelp_id } , i) => (
            <Row key={`sight-id-search-${i}`} style={{justifyContent: 'flex-start', margin: '.5rem'}}>
              <Column>
                <Number>{i + 1}</Number>
              </Column>
              <Column>
                <Description><Bold>{name && name}</Bold></Description>
                <Description>{address && address}</Description>
                <Row style={{ justifyContent: 'flex-start' }}>
                  {google_id &&
                    <DescriptionGrey
                      onClick={() => openUrl(`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${google_id}`)}
                    >
                      Google
                    </DescriptionGrey>
                  }
                  {yelp_id &&
                    <DescriptionGrey
                      onClick={() => openUrl(`https://www.yelp.com/biz/${yelp_id}`)}
                    >
                      Yelp
                    </DescriptionGrey>
                  }
                </Row>
              </Column>
            </Row>
          ))}
        </Column>
      )
    } else {
      return(
        <StandAloneContainer style={{alignItems: 'flex-start'}}>
          <NoDataText>
            {searchMsg}
          </NoDataText>
        </StandAloneContainer>
      )
    }
  }

  return (
    <Container>
      <SideNav view={view} handleView={handleView} />
      <Content>
        <Card>
          <Row style={{height: '4rem', justifyContent: 'flex-start', marginTop: '1rem'}}>
            <CardTitle><Icon><i className="fas fa-eye"></i></Icon>MerchantSight</CardTitle>
          </Row>
          <Row style={{height: '4rem', justifyContent: 'center'}}>
            <Input 
              placeholder='Search..'
              onChange={handleSightChange}
              name='text'
              value={search.text}
            />
            <Input 
              placeholder='City'
              onChange={handleSightChange}
              name='city'
              value={search.city}
              style={{ width: '8rem' }}
            />
            <Input 
              placeholder='Postal'
              onChange={handleSightChange}
              name='postal'
              value={search.postal}
              style={{ width: '6rem' }}
            />
            <Button onClick={() => handleSearch(search)}>
              <i className="fas fa-search"></i>
            </Button>
          </Row>
          <Row style={{ justifyContent: 'center' }}>
            <Description>Search text string from card statements. City and Postal are optional.</Description>
          </Row>
          {showResults()}
        </Card>
      </Content>
    </Container>
  );
}

export default Sight;

const Container = styled.div`
  display: flex;
  width: 100%;
`

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  width: 100%;
  height: 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Column = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const Icon = styled.div`
  color: ${({ theme }) => theme.colors.primary };
  padding: .3rem auto;
  margin-right: 1rem;
`

const Card = styled.div`
  ${({ theme }) => theme.animations.fadeIn };
  display: flex;
  flex-direction: column;
  border-radius: .25rem;
  background-color: #fff;
  padding: 0rem 2.5rem 2.5rem 2.5rem;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  height: 100%;
`

const CardTitle = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.text };
  font-size: 1.8em;
  font-weight: 600;
  align-items: center;
  padding: .5rem auto;
`

const Button = styled.button`
  color: #fff;
  background-color: ${({ theme }) => theme.colors.buttonPrimary };
  font-size: 1.1em;
  text-align: center;
  width: 2rem;
  padding: .4rem;
  height: 1.9rem;
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    background-color: #fff;
    color: ${({ theme }) => theme.colors.primary };
    border: 1px solid ${({ theme }) => theme.colors.primary };
  }
`

const Input = styled.input`
  height: 1rem;
  width: 18rem;
  margin-right: 1rem;
`

const StandAloneContainer = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
`

const NoDataText = styled.div`
  font-size: 1.4em;
  margin: 2rem;
`

const Number = styled.div`
  font-size: 1.4em;
  min-width: 1rem;
  color: ${({ theme }) => theme.colors.primary };
  font-weight: 700;
  margin-right: .5rem;
`

const Description = styled.div`
  font-size: 1.1em;
`

const DescriptionGrey = styled.div`
  font-size: 1em;
  color: ${({ theme }) => theme.colors.primary };
  cursor: pointer;
  margin-right: .5rem;
`

const Bold = styled.span`
  font-weight: 500;
  font-size: 1.2em;
`
